import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// Section components
import CallToAction from '../../components/sections/CallToAction';

// UI Components
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

// Data
import { caseStudies } from '../../data/mockData';

/**
 * Case Studies listing page component
 */
const CaseStudiesPage: React.FC = () => {
  // Group case studies by industry for filtering
  const industries = [...new Set(caseStudies.map(study => study.industry))];
  const solutions = [...new Set(caseStudies.map(study => study.solution))];
  
  return (
    <Layout>
      {/* Hero section */}
      <div className="relative overflow-hidden bg-neu-bg pt-28 pb-14">
        <div
          className="pointer-events-none absolute top-0 right-0 h-full flex items-center opacity-50 overflow-hidden"
          style={{ width: '38%' }}
        >
          <div
            className="relative flex items-center justify-center"
            style={{ width: 380, height: 380, marginLeft: 'auto', marginRight: '-80px' }}
          >
            <div className="absolute rounded-full" style={{ width: 380, height: 380, boxShadow: '10px 10px 22px rgb(163,177,198,0.5), -10px -10px 22px rgba(255,255,255,0.4)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full" style={{ width: 260, height: 260, boxShadow: 'inset 7px 7px 14px rgb(163,177,198,0.6), inset -7px -7px 14px rgba(255,255,255,0.5)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full" style={{ width: 140, height: 140, boxShadow: '7px 7px 14px rgb(163,177,198,0.6), -7px -7px 14px rgba(255,255,255,0.5)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full animate-float" style={{ width: 56, height: 56, background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)', boxShadow: '0 4px 16px rgba(108,99,255,0.45)' }} />
          </div>
        </div>
        <Container>
          <motion.div
            className="relative z-10 max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-neu-text mb-6 leading-tight">
              Client Success Stories
            </h1>
            <p className="text-lg md:text-xl text-neu-muted mb-8 max-w-2xl mx-auto leading-relaxed">
              Real results from our AI implementations across various industries
            </p>
          </motion.div>
        </Container>
      </div>
      
      {/* Filters Section */}
      <Section bgColor="bg-white" padding="md">
        <Container>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-neu-text">Filter:</span>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="text-sm">
                  All
                </Button>
                {industries.map((industry) => (
                  <Button key={industry} variant="outline" size="sm" className="text-sm">
                    {industry}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Main Content */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {caseStudies.map((study, index) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex"
                >
                  <Link to={study.url} className="w-full h-full">
                    <Card hover className="h-full flex flex-col">
                      <div className="aspect-video bg-neutral-200 rounded-t-lg overflow-hidden">
                        <img
                          src={study.imageSrc}
                          alt={study.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            img.style.display = 'none';
                            if (img.nextElementSibling) {
                              (img.nextElementSibling as HTMLElement).style.display = 'flex';
                            }
                          }}
                        />
                        <div
                          className="w-full h-full bg-gradient-to-br from-primary to-secondary opacity-70 flex items-center justify-center hidden"
                          style={{ display: 'flex' }}
                        >
                          <span className="text-white text-lg font-semibold">{study.industry}</span>
                        </div>
                      </div>
                      <div className="p-6 md:p-8 flex-grow flex flex-col">

                        <div className="mb-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            {study.solution}
                          </span>
                        </div>

                        <h3 className="text-xl font-semibold mb-3">{study.title}</h3>
                        <p className="text-neutral-600 mb-4 text-sm">{study.excerpt}</p>

                        <div className="mt-auto">
                          <h4 className="font-semibold text-xs text-neutral-800 mb-2 uppercase tracking-wide">Key Results:</h4>
                          <ul className="space-y-2 mb-4">
                            {study.results.slice(0, 2).map((result, idx) => (
                              <li key={idx} className="flex items-start text-sm text-neutral-600">
                                <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{result}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="pt-2 border-t border-neutral-100">
                            <span className="inline-flex items-center text-primary font-medium text-sm">
                              Read full case study
                              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Call to action */}
      <CallToAction
        title="Ready to become our next success story?"
        description="Schedule a consultation to discuss how our AI solutions can drive similar results for your business."
        ctaText="Book Your Free Consultation"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default CaseStudiesPage;